import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import * as d3 from 'd3';

import { teamsApi } from '../../../../../../../../services/api';
import LoadingSpinner from '../../../../../../../common/LoadingSpinner';
import { useAlert } from '../../../../../../../../contexts/AlertContext';

const TeamSelection = ({
  projectId,
  selectedTeamId,
  setSelectedTeamId,
  selectedEmployee
}) => {
  const [teamHierarchy, setTeamHierarchy] = useState(null);
  const [isTeamHierarchyLoading, setIsTeamHierarchyLoading] = useState(false);
  const svgRef = useRef();
  const { showAlert } = useAlert();

  useEffect(() => {
    fetchTeamHierarchy();
  }, [projectId]);

  const fetchTeamHierarchy = async () => {
    setIsTeamHierarchyLoading(true);
    try {
      const data = await teamsApi.getTeamHierarchy(projectId);
      const transformTeams = (team) => ({
        ...team,
        children: team.childTeams ? team.childTeams.map(transformTeams) : []
      });
      
      const rootTeam = data[0];
      if (rootTeam) {
        setTeamHierarchy(transformTeams(rootTeam));
      } else {
        setTeamHierarchy(null);
      }
    } catch (err) {
      showAlert('Failed to load team hierarchy.', 'error', err);
      console.error('Failed to load team hierarchy:', err);
    } finally {
      setIsTeamHierarchyLoading(false);
    }
  };

  useEffect(() => {
    if (!teamHierarchy || !svgRef.current) return;

    // Clear existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 30, right: 40, bottom: 10, left: 40 };
    const width = 800;
    const barHeight = 25;
    const treeIndent = 20;

    // Create SVG first
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .style("font", "12px sans-serif");

    // Create main group for tree
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create hierarchy and store original children
    const root = d3.hierarchy(teamHierarchy);
    root.descendants().forEach(d => {
      d._children = d.children;
      d.children = d._children;
    });

    const updateTree = (source) => {
      // Get visible nodes and links
      const nodes = root.descendants();
      const links = root.links();

      // Calculate height based on visible nodes
      const height = nodes.length * barHeight + margin.top + margin.bottom;

      // Update SVG height
      d3.select(svgRef.current)
        .attr("height", height);

      // Calculate positions
      let i = 0;
      root.eachBefore(d => {
        d.x = d.depth * treeIndent;
        d.y = i * barHeight;
        i++;
      });

      // Update links
      const link = g.selectAll(".link")
        .data(links, d => d.target.id || (d.target.id = Math.random()));

      const linkEnter = link.enter()
        .append("path")
        .attr("class", "link")
        .attr("fill", "none")
        .attr("stroke", "#999")
        .attr("stroke-opacity", 0.4)
        .attr("d", d => {
          const o = { x: source.x0 || source.x, y: source.y0 || source.y };
          return `M${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}`;
        });

      link.merge(linkEnter)
        .transition()
        .duration(300)
        .attr("d", d => {
          return `M${d.source.x},${d.source.y + barHeight / 2}
                  L${d.source.x},${d.target.y + barHeight / 2}
                  L${d.target.x},${d.target.y + barHeight / 2}`;
        });

      link.exit()
        .transition()
        .duration(300)
        .attr("d", d => {
          const o = { x: source.x, y: source.y };
          return `M${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}`;
        })
        .remove();

      // Update nodes
      const node = g.selectAll(".node")
        .data(nodes, d => d.id || (d.id = Math.random()));

      const nodeEnter = node.enter()
        .append("g")
        .attr("class", "node")
        .attr("transform", d => `translate(0,${source.y0 || source.y})`)
        .style("opacity", 0);

      // Add node group with click handler
      const nodeGroup = nodeEnter.append("g")
        .attr("class", "node-group")
        .style("cursor", "pointer")
        .on("click", function(event, d) {
          event.stopPropagation();
          setSelectedTeamId(d.data.id);
        });

      // Add expand/collapse button
      nodeEnter.append("g")
        .attr("class", "button-group")
        .attr("transform", d => `translate(${d.x - 15},${barHeight / 2})`)
        .style("cursor", d => d.depth === 0 ? "default" : "pointer")
        .style("display", d => d._children || d.children ? null : "none")
        .on("click", (event, d) => {
          event.stopPropagation();
          if (d.depth !== 0) {
            if (d.children) {
              d._children = d.children;
              d.children = null;
            } else {
              d.children = d._children;
              d._children = null;
            }
            updateTree(d);
          }
        })
        .each(function(d) {
          const group = d3.select(this);
          group.append("circle")
            .attr("r", 7)
            .attr("fill", "white")
            .attr("stroke", "#999");
          
          group.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("font-size", "10px")
            .text(d => d.children ? "−" : "+");
        });

      nodeGroup.each(function(d) {
        const node = d3.select(this);
        let totalWidth = d.x + 8;

        const textElement = node.append("text")
          .attr("x", totalWidth)
          .attr("y", barHeight / 2)
          .attr("dy", "0.31em")
          .attr("text-anchor", "start")
          .text(d.depth === 0 ? `Project: ${d.data.name}` : d.data.name)
          .style("fill", d.data.id === selectedTeamId ? "#2563EB" : "#1E293B")
          .style("font-weight", d.data.id === selectedTeamId ? "600" : "normal");

        totalWidth += textElement.node().getComputedTextLength() + 16;

        if (d.data.manager) {
          const managerText = node.append("text")
            .attr("x", totalWidth)
            .attr("y", barHeight / 2)
            .attr("dy", "0.31em")
            .attr("text-anchor", "start")
            .text(`(${d.data.manager.fullName})`)
            .style("fill", "#64748B")
            .style("font-size", "12px");
        }
      });

      const nodeUpdate = node.merge(nodeEnter)
        .transition()
        .duration(300)
        .attr("transform", d => `translate(0,${d.y})`)
        .style("opacity", 1);

      node.exit()
        .transition()
        .duration(300)
        .attr("transform", d => `translate(0,${source.y})`)
        .style("opacity", 0)
        .remove();

      nodes.forEach(d => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    updateTree(root);

  }, [teamHierarchy, selectedTeamId]);

  return (
    <Box sx={{ width: '100%', minHeight: '400px' }}>
      {isTeamHierarchyLoading ? (
        <LoadingSpinner />
      ) : !teamHierarchy ? (
        <Typography sx={{ textAlign: 'center', color: '#666' }}>
          No teams have been created yet.
        </Typography>
      ) : (
        <>
          <Typography sx={{ mb: 2, color: '#475569' }}>
            Select a team to attach {selectedEmployee?.fullName} to:
          </Typography>
          <svg ref={svgRef} style={{ width: '100%', minHeight: '400px' }}></svg>
        </>
      )}
    </Box>
  );
};

export default TeamSelection; 